<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import ClientSettingsAPI from 'custom/dashboard/api/clientSettings';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import { useClientSettingsLabels } from 'custom/dashboard/composables/zimobi/useClientSettingsLabels';
import { isFeaturePending } from 'custom/dashboard/helper/zimobi/clientSettingsHelper';
import ClientSettingsTabs from './ClientSettingsTabs.vue';
import ConfigurationDialog from './ConfigurationDialog.vue';

const { t } = useI18n();
const { featureLabel, fieldLabel } = useClientSettingsLabels();
const store = useStore();

const isFetching = ref(true);
const isUpdating = ref(false);
const client = ref({});
const features = ref({});
const settings = ref({});
// Backend-declared list of flags and the configuration each one takes. Rendering from
// it keeps ClientSettings::Registry the single place a new toggle is declared.
const schema = ref([]);
const configurationDialogRef = ref(null);

const createdAtLabel = computed(() => {
  if (!client.value.created_at) return '';
  return new Date(client.value.created_at).toLocaleDateString();
});

// Computed here rather than taken from the backend so the badge reacts to edits the admin
// has not saved yet.
const needsConfiguration = feature =>
  Boolean(features.value[feature.key]) &&
  isFeaturePending(feature, settings.value);

const hasConfigurableFeature = computed(() =>
  schema.value.some(
    feature => features.value[feature.key] && feature.config.length
  )
);

// A flat list of every flag is too long to scan, so they collapse into one section per
// group. Order follows the backend schema, so a new group needs no change here.
const groups = computed(() => {
  const byGroup = new Map();

  schema.value.forEach(feature => {
    if (!byGroup.has(feature.group)) byGroup.set(feature.group, []);
    byGroup.get(feature.group).push(feature);
  });

  return [...byGroup].map(([name, groupFeatures]) => {
    const enabled = groupFeatures.filter(f => features.value[f.key]).length;
    const pending = groupFeatures.filter(needsConfiguration).length;

    return {
      name,
      features: groupFeatures,
      pending,
      enabled,
      total: groupFeatures.length,
      label: t(`CLIENT_SETTINGS.GROUPS.${name.toUpperCase()}`),
    };
  });
});

const fetchClientSettings = async () => {
  isFetching.value = true;
  try {
    const { data } = await ClientSettingsAPI.get();
    client.value = data;
    features.value = { ...data.features };
    settings.value = { ...data.settings };
    schema.value = data.schema;
  } catch (error) {
    useAlert(t('CLIENT_SETTINGS.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

// The backend names the offending fields as keys rather than in its message, so the
// admin reads them in their own language instead of raw column names.
const updateErrorMessage = error => {
  const fields = error?.response?.data?.fields;
  if (!fields?.length) return t('CLIENT_SETTINGS.API.UPDATE_ERROR');

  return t('CLIENT_SETTINGS.API.MISSING_CONFIGURATION', {
    fields: fields.map(fieldLabel).join(', '),
  });
};

const saveSettings = async () => {
  isUpdating.value = true;
  try {
    const { data } = await ClientSettingsAPI.update({
      features: features.value,
      settings: settings.value,
    });
    client.value = data;
    features.value = { ...data.features };
    settings.value = { ...data.settings };
    schema.value = data.schema;
    await store.dispatch('accounts/get');
    useAlert(t('CLIENT_SETTINGS.API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(updateErrorMessage(error));
  } finally {
    isUpdating.value = false;
  }
};

// When set, the dialog narrows to a single feature — used when a toggle has just been
// switched on and the admin is being offered its configuration right away.
const focusedFeature = ref(null);

const openConfiguration = (key = null) => {
  focusedFeature.value = key;
  configurationDialogRef.value?.open();
};

// A flag switched on without its credentials is inert, and the admin would otherwise only
// find out from the badge after saving, so the form is offered on the spot.
const onToggle = (feature, value) => {
  features.value[feature.key] = value;
  if (value && feature.config.length) openConfiguration(feature.key);
};

// Persists the configuration together with the current toggles, so the flag switched on
// a moment ago and the credentials it needs reach the account in the same request.
const onConfigurationConfirm = async updated => {
  settings.value = { ...settings.value, ...updated };
  configurationDialogRef.value?.close();
  await saveSettings();
};

onMounted(fetchClientSettings);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('CLIENT_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="client.name || $t('CLIENT_SETTINGS.HEADER')"
        :description="$t('CLIENT_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #preBody>
      <ClientSettingsTabs class="mb-6" />
    </template>

    <template #body>
      <section class="flex flex-col gap-4">
        <h2 class="text-lg font-medium text-slate-900 dark:text-slate-25">
          {{ $t('CLIENT_SETTINGS.SUMMARY.TITLE') }}
        </h2>
        <dl
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="client-summary"
        >
          <div class="flex flex-col gap-1">
            <dt class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CLIENT_SETTINGS.SUMMARY.NAME') }}
            </dt>
            <dd
              class="text-base font-medium text-slate-900 dark:text-slate-25"
              data-testid="summary-name"
            >
              {{ client.name }}
            </dd>
          </div>
          <div class="flex flex-col gap-1">
            <dt class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CLIENT_SETTINGS.SUMMARY.CREATED_AT') }}
            </dt>
            <dd
              class="text-base font-medium text-slate-900 dark:text-slate-25"
              data-testid="summary-created-at"
            >
              {{ createdAtLabel }}
            </dd>
          </div>
          <div class="flex flex-col gap-1">
            <dt class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CLIENT_SETTINGS.SUMMARY.USERS') }}
            </dt>
            <dd
              class="text-base font-medium text-slate-900 dark:text-slate-25"
              data-testid="summary-users"
            >
              {{ client.users_count }}
            </dd>
          </div>
          <div class="flex flex-col gap-1">
            <dt class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CLIENT_SETTINGS.SUMMARY.AGENTS') }}
            </dt>
            <dd
              class="text-base font-medium text-slate-900 dark:text-slate-25"
              data-testid="summary-agents"
            >
              {{ client.agents_count }} / {{ client.administrators_count }}
            </dd>
          </div>
        </dl>
      </section>

      <section
        class="flex flex-col gap-4 pt-8 mt-8 border-t border-slate-75 dark:border-slate-700"
      >
        <h2 class="text-lg font-medium text-slate-900 dark:text-slate-25">
          {{ $t('CLIENT_SETTINGS.FEATURES.TITLE') }}
        </h2>

        <div class="flex flex-col max-w-5xl gap-3">
          <Accordion
            v-for="group in groups"
            :key="group.name"
            :title="group.label"
            :is-open="group.pending > 0"
            :data-testid="`group-${group.name}`"
          >
            <template #title>
              <span class="flex items-center gap-2">
                {{ group.label }}
                <span class="font-normal text-n-slate-11">
                  {{
                    $t('CLIENT_SETTINGS.GROUPS.SUMMARY', {
                      enabled: group.enabled,
                      total: group.total,
                    })
                  }}
                </span>
                <span
                  v-if="group.pending"
                  class="px-2 py-0.5 text-xs font-medium rounded-md text-n-amber-11 bg-n-amber-3"
                  :data-testid="`group-pending-${group.name}`"
                >
                  {{ $t('CLIENT_SETTINGS.GROUPS.PENDING', group.pending) }}
                </span>
              </span>
            </template>

            <div class="grid grid-cols-1 gap-x-10 lg:grid-cols-2">
              <div
                v-for="feature in group.features"
                :key="feature.key"
                class="flex items-center justify-between gap-2 py-2"
              >
                <span class="flex items-center gap-2">
                  <span class="text-base text-slate-800 dark:text-slate-100">
                    {{ featureLabel(feature.key) }}
                  </span>
                  <span
                    v-if="needsConfiguration(feature)"
                    class="px-2 py-0.5 text-xs font-medium rounded-md text-n-amber-11 bg-n-amber-3"
                    :data-testid="`needs-configuration-${feature.key}`"
                  >
                    {{ $t('CLIENT_SETTINGS.NEEDS_CONFIGURATION') }}
                  </span>
                </span>
                <Switch
                  :model-value="Boolean(features[feature.key])"
                  :data-testid="`toggle-${feature.key}`"
                  @update:model-value="value => onToggle(feature, value)"
                />
              </div>
            </div>
          </Accordion>
        </div>

        <div class="flex items-center gap-3">
          <NextButton
            :is-loading="isUpdating"
            :label="$t('CLIENT_SETTINGS.SAVE')"
            data-testid="save-client-settings"
            @click="saveSettings"
          />
          <NextButton
            variant="faded"
            color="slate"
            :label="$t('CLIENT_SETTINGS.CONFIGURE')"
            :disabled="!hasConfigurableFeature"
            data-testid="open-configuration"
            @click="openConfiguration()"
          />
        </div>
      </section>

      <ConfigurationDialog
        ref="configurationDialogRef"
        :schema="schema"
        :features="features"
        :model-value="settings"
        :focus-key="focusedFeature"
        :is-updating="isUpdating"
        @confirm="onConfigurationConfirm"
      />
    </template>
  </SettingsLayout>
</template>
